import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { XMarkIcon } from '@gto/icons-react/outline/x-mark'
import { Dialog } from '@gto/ui/dialog'
import { IconButton } from '@gto/ui/icon-button'
import { Typography } from '@gto/ui/typography'

type RootProps = {
  open: boolean
  onClose: () => void
  children: ReactNode
  width?: number
}

const Root = ({ open, onClose, children, width = 500 }: RootProps) => {
  const { formatMessage } = useIntl()

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup
          className="flex max-w-[90vw] flex-col gap-5 rounded-lg bg-paper-elevation4 p-6 shadow-9 sm:max-w-[90vw] sm:p-6"
          style={{ width }}
        >
          <IconButton
            aria-label={formatMessage({ id: 'common.close' })}
            className="absolute right-2.5 top-2.5 z-[1]"
            size="small"
            onClick={onClose}
          >
            <XMarkIcon
              height={18}
              width={18}
            />
          </IconButton>
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type HeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
}

const Header = ({ title, subtitle }: HeaderProps) => (
  <div className="flex flex-col gap-0.5">
    <Dialog.Title>{title}</Dialog.Title>
    {subtitle != null && (
      <Typography
        color="text.secondary"
        variant="body2"
      >
        {subtitle}
      </Typography>
    )}
  </div>
)

type BodyProps = {
  children: ReactNode
}

const Body = ({ children }: BodyProps) => <div className="flex flex-col gap-5">{children}</div>

type FooterProps = {
  children: ReactNode
}

const Footer = ({ children }: FooterProps) => (
  <div className="flex justify-end gap-2">{children}</div>
)

export const AppDialog = Object.assign(Root, {
  Header,
  Body,
  Footer,
})
